import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Award, Share2, Printer, ArrowLeft, BadgeCheck, Linkedin, Download } from "lucide-react";
import api from "@/lib/api";

export default function Certificate() {
  const { code } = useParams();
  const [cert, setCert] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    api.get(`/certificates/${code}`)
      .then((r) => { setCert(r.data); setState("ok"); })
      .catch(() => setState("missing"));
  }, [code]);

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: `${cert.course_title} — Certificate`, url }); return; } catch { /* fallthrough */ }
    }
    navigator.clipboard?.writeText(url);
    toast.success("Share link copied");
  };

  if (state === "loading") return <div className="p-24 text-center text-[color:var(--ink-muted)]">Loading certificate…</div>;
  if (state === "missing") return (
    <div className="mx-auto max-w-lg px-5 py-24 text-center" data-testid="certificate-missing">
      <Award className="mx-auto text-[color:var(--ink-muted)]" size={28} />
      <h1 className="mt-4 font-editorial text-3xl text-[color:var(--ink)]">Certificate not found</h1>
      <p className="mt-3 text-sm text-[color:var(--ink-body)]">This certificate link is invalid or has been revoked.</p>
      <Link to="/" className="btn-edge mt-6">Back home</Link>
    </div>
  );

  const backendBase = process.env.REACT_APP_BACKEND_URL || "";
  const issued = cert.issued_at ? new Date(cert.issued_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : "";
  const issuedDate = cert.issued_at ? new Date(cert.issued_at) : new Date();
  const linkedInUrl = `https://www.linkedin.com/profile/add?${new URLSearchParams({
    startTask: "CERTIFICATION_NAME",
    name: cert.course_title || "Course",
    organizationName: cert.site_name || "HK Forexedge",
    certUrl: window.location.href,
    certId: cert.code || "",
    issueYear: String(issuedDate.getFullYear()),
    issueMonth: String(issuedDate.getMonth() + 1),
  }).toString()}`;

  return (
    <div className="mx-auto max-w-4xl px-5 py-12 lg:px-8 lg:py-16" data-testid="certificate-page">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3 print:hidden">
        <Link to="/store/courses" className="inline-flex items-center gap-1.5 text-xs text-[color:var(--ink-muted)] hover:text-[color:var(--evergreen)]"><ArrowLeft size={12} /> Courses</Link>
        <div className="flex flex-wrap gap-2">
          <a href={linkedInUrl} target="_blank" rel="noreferrer" data-testid="certificate-linkedin" className="inline-flex items-center gap-1.5 rounded-full bg-[#0a66c2] px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90"><Linkedin size={14} /> Add to LinkedIn</a>
          <a href={`${backendBase}/api/certificates/${cert.code}/pdf`} data-testid="certificate-download-pdf" className="btn-edge text-sm"><Download size={14} /> Download PDF</a>
          <button onClick={share} data-testid="certificate-share" className="btn-edge-ghost text-sm"><Share2 size={14} /> Share</button>
          <button onClick={() => window.print()} data-testid="certificate-print" className="btn-edge-ghost text-sm"><Printer size={14} /> Print</button>
        </div>
      </div>

      <div className="relative overflow-hidden rounded-2xl border-2 border-[color:var(--evergreen)] bg-[color:var(--paper)] p-8 shadow-xl sm:p-14" data-testid="certificate-card">
        <div className="pointer-events-none absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, var(--evergreen) 0, transparent 40%), radial-gradient(circle at 80% 80%, var(--evergreen) 0, transparent 40%)" }} />
        <div className="relative text-center">
          <img src={cert.logo_url || "/logo.png"} alt={cert.site_name || "logo"} data-testid="certificate-logo"
            className="mx-auto mb-6 h-9 w-auto object-contain" onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "/logo-mark.png"; }} />
          <span className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-[color:var(--evergreen-soft)] text-[color:var(--evergreen)]"><Award size={30} /></span>
          <p className="mt-6 text-xs font-semibold uppercase tracking-[0.35em] text-[color:var(--ink-muted)]">Certificate of Completion</p>
          <p className="mt-8 text-sm text-[color:var(--ink-body)]">This certifies that</p>
          <h1 className="mt-2 font-editorial text-4xl font-semibold text-[color:var(--ink)] sm:text-5xl" data-testid="certificate-name">{cert.user_name}</h1>
          <p className="mt-6 text-sm text-[color:var(--ink-body)]">has successfully completed the course</p>
          <h2 className="mt-2 font-editorial text-2xl font-medium text-[color:var(--evergreen)] sm:text-3xl" data-testid="certificate-course">{cert.course_title}</h2>

          {(cert.signature_url || cert.instructor_name) && (
            <div className="mx-auto mt-10 flex w-fit flex-col items-center" data-testid="certificate-signature">
              {cert.signature_url && (
                <img src={`${backendBase}${cert.signature_url}`} alt="signature" className="h-14 w-auto max-w-[220px] object-contain" />
              )}
              <div className="mt-1 border-t border-[color:var(--ink)] px-10 pt-1.5 text-center">
                <p className="text-sm font-semibold text-[color:var(--ink)]">{cert.instructor_name || "Course Instructor"}</p>
                <p className="text-[10px] font-semibold uppercase tracking-widest text-[color:var(--ink-muted)]">Instructor</p>
              </div>
            </div>
          )}

          <div className="mx-auto mt-10 flex max-w-md items-center justify-between border-t border-[color:var(--ink-hairline)] pt-6 text-left">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-widest text-[color:var(--ink-muted)]">Issued</p>
              <p className="text-sm text-[color:var(--ink)]">{issued}</p>
            </div>
            <div className="text-right">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-[color:var(--ink-muted)]">Issued by</p>
              <p className="text-sm text-[color:var(--ink)]">{cert.site_name}</p>
            </div>
          </div>
          <div className="mt-6 flex flex-col items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--evergreen-soft)] px-3 py-1 text-[11px] text-[color:var(--evergreen)]" data-testid="certificate-verify">
              <BadgeCheck size={13} /> Verified · ID {cert.code}
            </span>
            <Link to={`/verify?code=${cert.code}`} className="text-[11px] text-[color:var(--ink-muted)] underline hover:text-[color:var(--evergreen)] print:hidden" data-testid="certificate-verify-link">Verify this certificate</Link>
            <div className="mt-2 flex flex-col items-center" data-testid="certificate-qr">
              <img src={`${backendBase}/api/certificates/${cert.code}/qr`} alt="Scan to verify" className="h-24 w-24 rounded-md bg-white p-1" />
              <p className="mt-1 text-[10px] uppercase tracking-widest text-[color:var(--ink-muted)]">Scan to verify</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
